import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface FormValidationContextType {
  isValid: boolean;
  setIsValid: (valid: boolean) => void;
  validationTrigger: 'onSubmit' | 'onChange'; // This mirrors, but doesn't overlap with the AntD Form validation trigger concept
  setValidationTrigger: (trigger: 'onSubmit' | 'onChange') => void;
  isSubmitting: boolean;
  setIsSubmitting: (loading: boolean) => void;
  onSubmitForm: () => void;
  addOnSubmitFormListener: (callback: () => void) => void;
  removeOnSubmitFormListener: (callback: () => void) => void;
}

export const FormValidationContext = createContext<FormValidationContextType | null>(null);

export const FormValidationProvider = ({ children }: { children: React.ReactNode }) => {
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationTrigger, setValidationTrigger] = useState<'onSubmit' | 'onChange'>('onChange');

  const onSubmitFormListeners = useRef(new Set<() => void>());

  const onSubmitForm = useCallback(() => {
    onSubmitFormListeners.current.forEach(listener => listener()); // Notify all registered listeners
  }, []);

  const addOnSubmitFormListener = useCallback((callback: () => void) => {
    onSubmitFormListeners.current.add(callback);
  }, []);

  const removeOnSubmitFormListener = useCallback((callback: () => void) => {
    onSubmitFormListeners.current.delete(callback);
  }, []);

  return (
    <FormValidationContext.Provider
      value={{
        isValid,
        setIsValid,
        validationTrigger,
        setValidationTrigger,
        isSubmitting,
        setIsSubmitting,
        onSubmitForm,
        addOnSubmitFormListener,
        removeOnSubmitFormListener,
      }}
    >
      {children}
    </FormValidationContext.Provider>
  );
};

export const useFormValidation = () => {
  const context = useContext(FormValidationContext);
  if (!context) {
    throw new Error('useFormValidation must be used within a FormValidationProvider');
  }
  return context;
};

export const useOnNext = (callback: () => void) => {
  const { addOnSubmitFormListener, removeOnSubmitFormListener } = useFormValidation();

  useEffect(() => {
    addOnSubmitFormListener(callback); // Add listener
    return () => {
      removeOnSubmitFormListener(callback); // Cleanup on unmount
    };
  }, [callback, addOnSubmitFormListener, removeOnSubmitFormListener]);
};
